import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import Section from '../../components/Main/Section/Section';
import { useTranslation } from 'react-i18next';
import { CurrentViewContext } from '../../providers/CurrentViewProvider';
import {
  Box,
  Checkbox,
  IconButton,
  LinearProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import CardContent from '@mui/material/CardContent';
import WidgetGauge from '../../components/Widgets/WidgetGauge';
import WidgetLineChart from '../../components/Widgets/WidgetLineChart';
import WidgetReviewList from '../../components/Widgets/WidgetReviewList';
import WidgetRatingList from '../../components/Widgets/WidgetRatingList';
import WidgetReviewSummary from '../../components/Widgets/WidgetReviewSummary';
import WidgetAlerts from '../../components/Widgets/WidgetAlerts';
import WidgetSurveyTool from '../../components/Widgets/WidgetSurveyTool';
import WidgetRatingCount from '../../components/Widgets/WidgetRatingCount';
import { Layout, Layouts, Responsive, WidthProvider } from 'react-grid-layout';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import FilterListIcon from '@mui/icons-material/FilterList';
import { AuthContext } from '../../providers/AuthProvider';
import { Link as RouterLink } from 'react-router-dom';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface WidgetConfig {
  reviews?: { id: string; content: string }[];
}

interface Widget {
  id: string;
  type:
    | 'WidgetGauge'
    | 'WidgetLineChart'
    | 'WidgetReviewList'
    | 'WidgetRatingList'
    | 'WidgetReviewSummary'
    | 'WidgetAlerts'
    | 'WidgetSurveyTool'
    | 'WidgetRatingCount';
  label: string;
  layout?: Layout;
  tooltip?: string;
  config?: WidgetConfig;
  link?: string;
  visible?: boolean;
}

interface WidgetProps {
  widget: Widget;
}

const Dashboard: FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { setCurrentView } = useContext(CurrentViewContext);
  const { hasRole } = useContext(AuthContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [widgets, setWidgets] = useState<Widget[]>([]);
  const [layouts, setLayouts] = useState<Layouts>({});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleWidgetVisibilityChange = (widgetId: string) => {
    setWidgets((prevWidgets) => {
      const updatedWidgets = prevWidgets.map((widget) =>
        widget.id === widgetId ? { ...widget, visible: !widget.visible } : widget
      );
      const widgetVisibility = updatedWidgets.reduce((acc, widget) => {
        acc[widget.id] = widget.visible;
        return acc;
      }, {} as Record<string, boolean | undefined>);

      saveToLS('widgetVisibility', widgetVisibility);
      return updatedWidgets;
    });
  };

  const getFromLS = (key: string) => {
    let ls: any = {};
    if (global.localStorage) {
      const stored = global.localStorage.getItem('repDashboardLayout');
      if (stored) {
        try {
          ls = JSON.parse(stored) || {};
        } catch (e) {
          /*Ignore*/
        }
      }
    }
    return ls[key];
  };

  const saveToLS = (key: string, value: any) => {
    if (global.localStorage) {
      const stored = global.localStorage.getItem('repDashboardLayout');
      let ls = {};
      if (stored) {
        try {
          ls = JSON.parse(stored) || {};
        } catch (e) {
          /*Ignore*/
        }
      }

      global.localStorage.setItem(
        'repDashboardLayout',
        JSON.stringify({
          ...ls,
          [key]: value,
        })
      );
    }
  };

  useEffect(() => {
    const fetchWidgets = async () => {
      const widgets: Widget[] = [];

      widgets.push({
        id: 'w1',
        type: 'WidgetGauge',
        label: 'dashboard_repscore_label',
        tooltip: 'dashboard_repscore_tooltip',
        visible: true,
      });
      widgets.push({
        id: 'w2',
        type: 'WidgetLineChart',
        label: 'dashboard_repscoredev_label',
        visible: true,
      });

      widgets.push({
        id: 'w3',
        type: 'WidgetRatingList',
        label: 'dashboard_monitorings_label',
        tooltip: 'dashboard_monitorings_tooltip',
        link: '/monitoring/platforms',
        visible: true,
      });
      if (hasRole('can-use-ai')) {
        widgets.push({
          id: 'w4',
          type: 'WidgetReviewList',
          label: 'dashboard_cm_label',
          link: '/communitymanagement/commentings',
          visible: true,
        });
      }
      widgets.push({
        id: 'w5',
        type: 'WidgetReviewSummary',
        label: 'dashboard_reviews_label',
        link: '/monitoring/reviews',
        tooltip: 'dashboard_reviews_tooltip',
        visible: true,
      });
      /*
            widgets.push({
              id: 6,
              type: 'WidgetAlerts',
              label: t('dashboard_alerts_label'),
              visible: true,
            });*/
      if (hasRole('can-use-survey')) {
        widgets.push({
          id: 'w7',
          type: 'WidgetSurveyTool',
          label: 'dashboard_surveytool_label',
          link: '/research/surveytool/guide',
          visible: true,
        });
      }
      widgets.push({
        id: 'w8',
        type: 'WidgetRatingCount',
        label: 'widget_ratingcount_title',
        link: '/monitoring/reviews',
        visible: true,
      });
      const cols = 3;
      widgets.forEach((w: Widget, i: number) => {
        widgets[i].layout = {
          i: w.id.toString(),
          x: i % cols,
          y: Math.ceil(i % cols),
          w: 1,
          h: 1,
        };
      });

      // Load widget visibility from localStorage
      const storedVisibility = getFromLS('widgetVisibility');
      if (storedVisibility) {
        // Apply stored visibility settings to widgets
        widgets.forEach((widget) => {
          if (storedVisibility[widget.id] !== undefined) {
            widget.visible = storedVisibility[widget.id];
          }
        });
      }

      const storedLayouts = getFromLS('layouts');
      if (storedLayouts) {
        setLayouts(storedLayouts);
        if (storedLayouts && storedLayouts.lg) {
          storedLayouts.lg.forEach((wl: any) => {
            widgets.forEach((widget) => {
              if (widget.id === wl.i) {
                widget.layout = wl;
              }
            });
          });
        }
      } else {
        setLayouts({
          lg: widgets.map((widget) => widget.layout || { i: '', x: 0, y: 0, w: 1, h: 1 }),
        });
      }
      setWidgets(widgets);
      setLoading(false);
    };

    fetchWidgets();
  }, [hasRole]);

  useEffect(() => {
    setCurrentView(t('nav_dashboard'));
    document.title = `Reputativ ${t('nav_dashboard')}`;
  }, [setCurrentView, t]);

  const createElement = useCallback((widget: Widget) => {
    const components: Record<Widget['type'], FC<WidgetProps>> = {
      WidgetGauge: WidgetGauge,
      WidgetLineChart: WidgetLineChart,
      WidgetReviewList: WidgetReviewList,
      WidgetRatingList: WidgetRatingList,
      WidgetReviewSummary: WidgetReviewSummary,
      WidgetAlerts: WidgetAlerts,
      WidgetSurveyTool: WidgetSurveyTool,
      WidgetRatingCount: WidgetRatingCount,
    };
    if (components[widget.type]) {
      return React.createElement(components[widget.type], {
        widget: widget,
      });
    }
    return <Box>The widget {widget.type} has not been defined yet.</Box>;
  }, []);

  const onLayoutChange = (l1: any, ls: any) => {
    saveToLS('layouts', ls);
    //setLayouts(ls);
  };

  return (
    <Section>
      {loading ? (
        <Box>
          <LinearProgress sx={{ mt: theme.spacing(1), mb: theme.spacing(2) }} />
        </Box>
      ) : (
        <Box sx={{ position: 'relative' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Box
              onClick={handleFilterClick}
              sx={{
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                pl: 1,
                color: theme.palette.text.secondary,
                borderWidth: 'thin',
                borderStyle: 'solid',
                fontSize: '0.9rem',
                borderColor: theme.palette.divider,
                borderRadius: `${theme.shape.borderRadius}px`,
              }}
            >
              <span>{t('dashboard_config')}</span>
              <Tooltip title={t('filter_widgets')}>
                <IconButton
                  size="small"
                  aria-controls={open ? 'widget-filter-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <FilterListIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Menu
              anchorEl={anchorEl}
              id="widget-filter-menu"
              open={open}
              onClose={handleFilterClose}
              onClick={(e) => e.stopPropagation()}
              slotProps={{
                paper: {
                  elevation: 0,
                  sx: {
                    minWidth: 250,
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: 1.5,
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      top: 0,
                      right: 14,
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateY(-50%) rotate(45deg)',
                      zIndex: 0,
                    },
                  },
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <Typography sx={{ p: 2, fontWeight: 'bold' }}>{t('show_widgets')}</Typography>
              {widgets.map((widget) => (
                <MenuItem
                  key={widget.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleWidgetVisibilityChange(widget.id);
                  }}
                >
                  <Checkbox checked={widget.visible} />
                  <Typography>{t(widget.label as any)}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
          <ResponsiveGridLayout
            className="layout"
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 3, md: 2, sm: 1, xs: 1, xxs: 1 }}
            rowHeight={360}
            containerPadding={[20, 20]}
            isDraggable={true}
            draggableHandle=".draggable-handle"
            onLayoutChange={onLayoutChange}
            compactType={'horizontal'}
          >
            {widgets
              .filter((widget) => widget.visible)
              .map((widget) => (
                <Box
                  className={'widget-' + widget.type}
                  data-grid={widget.layout}
                  key={widget.id}
                  sx={{
                    userSelect: 'none',
                    minWidth: '400px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    boxShadow:
                      'rgba(0, 0, 0, 0.2) 0px 2px 1px -1px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 1px 3px 0px',
                    borderRadius: `${theme.shape.borderRadius}px`,
                    backgroundColor: theme.palette.background.paper,
                  }}
                >
                  <CardContent>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: '.5rem',
                      }}
                    >
                      <Typography sx={{ flexGrow: 1, fontWeight: 'bold' }}>
                        {t(widget.label as any)}
                        {widget.tooltip && (
                          <Tooltip
                            title={t(widget.tooltip as any)}
                            arrow
                          >
                            <InfoIcon
                              fontSize="small"
                              sx={{ color: theme.palette.primary.main }}
                            />
                          </Tooltip>
                        )}
                      </Typography>
                      {widget.link && (
                        <Box
                          sx={{
                            display: 'flex',
                            gap: '3px',
                          }}
                        >
                          <Tooltip
                            title={t('details')}
                            arrow
                          >
                            <RouterLink to={widget.link}>
                              <OpenInNewIcon sx={{ color: theme.palette.primary.main }} />
                            </RouterLink>
                          </Tooltip>
                        </Box>
                      )}

                      <OpenWithIcon
                        className="draggable-handle"
                        sx={{ cursor: 'move', color: theme.palette.grey['400'] }}
                      />
                    </Box>
                    {createElement(widget)}
                  </CardContent>
                </Box>
              ))}
          </ResponsiveGridLayout>
        </Box>
      )}
    </Section>
  );
};

export default Dashboard;
